import Input from "../components/inputs/Input";
import { AiOutlineLoading } from "react-icons/ai";
import { Alert, Button, Tabs, Spinner, Tooltip } from "flowbite-react";
import useCEP from "../hooks/useCEP";
import { useForm, Controller } from "react-hook-form";
import { USERS } from "../config/apiConfig";
import axios from "../config/axiosInstance";
import { useEffect, useMemo, useState } from "react";
import { useApplication } from "../contexts/ApplicationContext";
import { useQuery } from "react-query";
import CPFInput from "../components/inputs/CPFInput";
import PhoneInput from "../components/inputs/PhoneInput";
import { HiAdjustments, HiUserCircle } from "react-icons/hi";
import { FiUser, FiPhone, FiMapPin } from "react-icons/fi";

const onlyDigits = (v) => (v || "").replace(/\D+/g, "");
const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const isValidPhoneBR = (mask) => {
  const d = onlyDigits(mask);
  return d.length === 10 || d.length === 11;
};
const isValidCPF = (cpfMask) => {
  const cpf = onlyDigits(cpfMask || "");
  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;
  let s = 0;
  for (let i = 0; i < 9; i++) s += parseInt(cpf[i]) * (10 - i);
  let r = (s * 10) % 11;
  if (r === 10 || r === 11) r = 0;
  if (r !== parseInt(cpf[9])) return false;
  s = 0;
  for (let i = 0; i < 10; i++) s += parseInt(cpf[i]) * (11 - i);
  r = (s * 10) % 11;
  if (r === 10 || r === 11) r = 0;
  return r === parseInt(cpf[10]);
};

const Section = ({ icon: Icon, title, children, className = "" }) => (
  <div
    className={`mb-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm ${className}`}
  >
    <div className="mb-4 flex items-center gap-2 text-base font-semibold text-slate-800">
      <Icon className="shrink-0" /> {title}
    </div>
    {children}
  </div>
);

const MyProfilePage = () => {
  const { user } = useApplication();

  // --- Profile form (dados pessoais/endereço)
  const {
    register: registerProfile,
    handleSubmit: handleSubmitProfile,
    control: controlProfile,
    setValue: setValueProfile,
    getValues: getValuesProfile,
    reset: resetProfile,
    setError: setErrorProfile,
    formState: { errors: errorsProfile },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      name: "",
      cpf: "",
      email: "",
      phoneNumber: "",
      birthday: "",
      gender: "",
      addressPostalCode: "",
      address: "",
      addressNumber: "",
      addressNeighborhood: "",
      addressCity: "",
    },
  });

  // --- Password form (apenas senha)
  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    setValue: setValuePassword,
    reset: resetPassword,
    formState: { errors: errorsPassword },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      password: "",
      password_confirm: "",
    },
  });

  // loading/alerts independentes
  const [submittingProfile, setSubmittingProfile] = useState(false);
  const [submittingPassword, setSubmittingPassword] = useState(false);
  const [alertProfile, setAlertProfile] = useState({
    show: false,
    message: "",
    type: "",
  });
  const [alertPassword, setAlertPassword] = useState({
    show: false,
    message: "",
    type: "",
  });

  const { isLoading, data: professional } = useQuery(
    ["professional", user],
    async () => {
      if (user?.id) {
        const { data: item } = await axios.get(`${USERS.GET_USER}/${user.id}`);
        return item.data;
      }
    }
  );

  // CEP integrado ao form de perfil
  const { setCep, address, loading: loadingCEP } = useCEP();

  // validações do form de perfil
  const rulesProfile = useMemo(
    () => ({
      name: { required: "Informe o nome" },
      email: {
        required: "Informe o e-mail",
        validate: (v) => (isValidEmail(v) ? true : "E-mail inválido"),
      },
      cpf: {
        validate: (v) => (v ? (isValidCPF(v) ? true : "CPF inválido") : true),
      },
      phoneNumber: {
        required: "Informe o telefone",
        validate: (v) => (isValidPhoneBR(v) ? true : "Telefone inválido"),
      },
    }),
    []
  );

  // preencher endereço ao resolver CEP
  useEffect(() => {
    setValueProfile("address", address?.logradouro || "");
    setValueProfile("addressNeighborhood", address?.bairro || "");
    setValueProfile("addressCity", address?.localidade || "");
  }, [address, setValueProfile]);

  // carregar perfil no form de perfil
  useEffect(() => {
    if (professional) {
      Object.keys(professional).forEach((k) => {
        if (k in (getValuesProfile() || {}))
          setValueProfile(k, professional[k] ?? "");
      });
    }
  }, [professional, setValueProfile, getValuesProfile]);

  const onClickSearchCEP = () => {
    const digits = onlyDigits(getValuesProfile("addressPostalCode"));
    if (digits.length < 8) {
      setErrorProfile("addressPostalCode", {
        type: "manual",
        message: "CEP inválido (8 dígitos)",
      });
      return;
    }
    setCep(digits);
  };

  const handleCEPKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onClickSearchCEP();
    }
  };

  const toastProfile = (message, type = "success") => {
    setAlertProfile({ show: true, message, type });
    setTimeout(
      () => setAlertProfile({ show: false, message: "", type: "" }),
      2200
    );
  };

  const toastPassword = (message, type = "success") => {
    setAlertPassword({ show: true, message, type });
    setTimeout(
      () => setAlertPassword({ show: false, message: "", type: "" }),
      2200
    );
  };

  // submit do PERFIL (somente dados de perfil)
  const onSubmitProfile = async (data) => {
    try {
      setSubmittingProfile(true);
      const payload = {
        ...data,
        cpf: data.cpf ? onlyDigits(data.cpf) : null,
        phoneNumber: data.phoneNumber ? onlyDigits(data.phoneNumber) : null,
        addressPostalCode: data.addressPostalCode
          ? onlyDigits(data.addressPostalCode)
          : null,
      };
      await axios.put(`${USERS.GET_USER}/${user.id}`, payload);
      toastProfile("Informações atualizadas com sucesso!", "success");
    } catch (error) {
      toastProfile("Erro ao tentar atualizar informações!", "failure");
    } finally {
      setSubmittingProfile(false);
    }
  };

  // submit da SENHA (somente senha)
  const onSubmitPassword = async (data) => {
    try {
      setSubmittingPassword(true);
      if (data.password !== data.password_confirm) {
        throw new Error("Senhas divergentes");
      }
      await axios.put(`${USERS.GET_USER}/${user.id}`, {
        id: user.id,
        password: data.password,
      });
      resetPassword(); // limpa campos
      toastPassword("Senha atualizada com sucesso!", "success");
    } catch (error) {
      toastPassword(
        error?.message || "Erro ao tentar atualizar senha!",
        "failure"
      );
    } finally {
      setSubmittingPassword(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center rounded-3xl border border-slate-200 bg-white p-10">
        <Spinner aria-label="Carregando perfil..." />
      </div>
    );
  }

  return (
    <div className="mx-auto">
      <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm md:p-6">
        <Tabs aria-label="Perfil" style="default">
          {/* ===================== TAB PERFIL ===================== */}
          <Tabs.Item active title="Meu Perfil" icon={HiUserCircle}>
            {/* Form de PERFIL isolado */}
            <form onSubmit={handleSubmitProfile(onSubmitProfile)}>
              <div className="mt-2">
                {/* Identificação */}
                <Section icon={FiUser} title="Identificação">
                  <div className="grid gap-4 md:grid-cols-2">
                    <Input
                      type="text"
                      label="Nome *"
                      name="name"
                      required
                      register={registerProfile}
                      rules={rulesProfile.name}
                      error={errorsProfile?.name?.message}
                    />
                    <Input
                      type="email"
                      label="E-mail *"
                      name="email"
                      required
                      register={registerProfile}
                      rules={rulesProfile.email}
                      error={errorsProfile?.email?.message}
                    />
                  </div>
                </Section>

                {/* Contato & Documento */}
                <Section icon={FiPhone} title="Contato & Documento">
                  <div className="grid gap-4 md:grid-cols-2">
                    <Controller
                      name="phoneNumber"
                      control={controlProfile}
                      rules={rulesProfile.phoneNumber}
                      render={({ field, fieldState }) => (
                        <PhoneInput
                          type="text"
                          label="Telefone *"
                          name={field.name}
                          value={field.value ?? ""}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          required
                          error={fieldState?.error?.message}
                        />
                      )}
                    />
                    <Controller
                      name="cpf"
                      control={controlProfile}
                      rules={rulesProfile.cpf}
                      render={({ field, fieldState }) => (
                        <CPFInput
                          type="text"
                          label="CPF"
                          name={field.name}
                          value={field.value ?? ""}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          error={fieldState?.error?.message}
                        />
                      )}
                    />
                  </div>
                </Section>

                {/* Endereço */}
                <Section icon={FiMapPin} title="Endereço">
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="md:col-span-2">
                      <Input
                        type="text"
                        label="CEP"
                        name="addressPostalCode"
                        register={registerProfile}
                        error={errorsProfile?.addressPostalCode?.message}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            onClickSearchCEP();
                          }
                        }}
                      />
                    </div>
                    <div className="flex items-end">
                      <Tooltip content="Busca automática de logradouro/bairro/cidade">
                        <Button
                          type="button"
                          onClick={onClickSearchCEP}
                          className="w-full md:w-auto"
                          isProcessing={loadingCEP}
                          processingSpinner={
                            <AiOutlineLoading className="h-5 w-5 animate-spin" />
                          }
                        >
                          Buscar CEP
                        </Button>
                      </Tooltip>
                    </div>
                  </div>

                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <Input
                      type="text"
                      label="Endereço"
                      name="address"
                      register={registerProfile}
                    />
                    <Input
                      type="text"
                      label="Número"
                      name="addressNumber"
                      register={registerProfile}
                    />
                    <Input
                      type="text"
                      label="Bairro"
                      name="addressNeighborhood"
                      register={registerProfile}
                    />
                    <Input
                      type="text"
                      label="Cidade"
                      name="addressCity"
                      register={registerProfile}
                    />
                  </div>
                </Section>

                {/* Footer fixo (ações) */}
                <div className="sticky bottom-2 z-10 mt-6 rounded-2xl border border-slate-200 bg-white/80 p-3 backdrop-blur supports-[backdrop-filter]:bg-white/60">
                  <div className="flex items-center justify-end gap-3">
                    <Button
                      color="light"
                      type="button"
                      onClick={() => resetProfile(professional)}
                      disabled={submittingProfile}
                    >
                      Desfazer alterações
                    </Button>
                    <Button
                      type="submit"
                      className="primary"
                      isProcessing={submittingProfile}
                      processingSpinner={
                        <AiOutlineLoading className="h-6 w-6 animate-spin" />
                      }
                      disabled={submittingProfile}
                    >
                      Atualizar perfil
                    </Button>
                  </div>
                  {alertProfile.show && (
                    <Alert className="mt-3" color={alertProfile.type}>
                      <span className="font-medium">
                        {alertProfile.message}
                      </span>
                    </Alert>
                  )}
                </div>
              </div>
            </form>
          </Tabs.Item>

          {/* ===================== TAB SENHA ===================== */}
          <Tabs.Item title="Trocar senha" icon={HiAdjustments}>
            {/* Form de SENHA isolado */}
            <form onSubmit={handleSubmitPassword(onSubmitPassword)}>
              <div className="mt-2">
                <Section icon={FiUser} title="Segurança">
                  <div className="grid gap-4">
                    <Input
                      type="password"
                      label="Nova senha *"
                      name="password"
                      register={registerPassword}
                      error={errorsPassword?.password?.message}
                    />
                    <Input
                      type="password"
                      label="Confirmar senha *"
                      name="password_confirm"
                      register={registerPassword}
                      error={errorsPassword?.password_confirm?.message}
                    />

                    <div className="rounded-xl bg-slate-50 p-3 text-xs text-slate-600">
                      Dica: use ao menos 8 caracteres, misturando letras e
                      números.
                    </div>

                    <div className="flex items-center justify-end gap-3">
                      <Button
                        color="light"
                        type="button"
                        onClick={() => {
                          resetPassword();
                        }}
                        disabled={submittingPassword}
                      >
                        Limpar
                      </Button>
                      <Button
                        type="submit"
                        className="primary"
                        isProcessing={submittingPassword}
                        processingSpinner={
                          <AiOutlineLoading className="h-6 w-6 animate-spin" />
                        }
                        disabled={submittingPassword}
                      >
                        Atualizar senha
                      </Button>
                    </div>

                    {alertPassword.show && (
                      <Alert className="mt-3" color={alertPassword.type}>
                        <span className="font-medium">
                          {alertPassword.message}
                        </span>
                      </Alert>
                    )}
                  </div>
                </Section>
              </div>
            </form>
          </Tabs.Item>
        </Tabs>
      </div>
    </div>
  );
};

export default MyProfilePage;
